<script setup lang="ts">
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import RlUploadPdf from '@/components/common/upload/RlUploadPdf.vue'
import { usePremiumInvoices } from '@/composables/asicw/premium-invoices'
import { NotifyFail } from '@/helpers/core/notifications'
import { getPolicyGroups } from '@/services/asicw/policies.service'
import type { ICreatePremiumInvoicePayload } from '@/types/asicw/invoices'
import { InvoiceStatus, InvoiceType } from '@/types/asicw/invoices'
import { PolicyStatus, type IPolicyGroup } from '@/types/asicw/policies'
import { Icon } from '@iconify/vue'
import type { FormInstance, UploadFile } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { computed, reactive, ref, useTemplateRef, watch } from 'vue'

const emit = defineEmits<{
  (e: 'closed'): void
}>()

const dialogVisible = defineModel<boolean>()

const formRef = ref<FormInstance>()
const uploadRef = useTemplateRef<InstanceType<typeof RlUploadPdf>>('uploadRef')
const dialogTitle = 'Create Invoice'

// Replace fileList ref with uploadFile
const uploadFile = ref<UploadFile>()

const { createPremiumInvoiceMutation } = usePremiumInvoices()
const { mutateAsync: handleCreateInvoice, isPending: isCreatePending } = createPremiumInvoiceMutation

// Initialize form with invoice data when available
const form = reactive<ICreatePremiumInvoicePayload>({
  policy_group_id: undefined,
  type: '',
  issue_date: '',
  due_date: '',
  policies_quantity: undefined,
  policy_group_premium: undefined,
  policy_underwriting_fee: undefined,
  commission_percentage: undefined,
  invoice_file: undefined as File | undefined,
  status: InvoiceStatus.READY_TO_SEND,
})

const selectedPolicyGroup = ref<IPolicyGroup>()

// Policy group search state
const policyGroupSearchLoading = ref<boolean>(false)
const policyGroupSearchResults = ref<OptionType[]>([])

// Store the number of active policies for calculations
const activePoliciesCount = ref<number>(0)

// Computed property to get/set policy group from the form
const policyGroupBinding = computed({
  get: () => selectedPolicyGroup.value,
  set: (policyGroup: IPolicyGroup | undefined) => {
    selectedPolicyGroup.value = policyGroup
    form.policy_group_id = policyGroup?.id || undefined
    activePoliciesCount.value = policyGroup?.policies_by_status?.[PolicyStatus.Active] || 0
  },
})

// Computed properties for calculations
const totalPremium = computed(() => {
  return (form.policy_group_premium ?? 0) * (form.policies_quantity ?? 0)
})

const totalUnderwritingFee = computed(() => {
  return (form.policy_underwriting_fee ?? 0) * (form.policies_quantity ?? 0)
})

const totalCommission = computed(() => {
  if (form.commission_percentage && form.commission_percentage > 0) {
    return (totalPremium.value * form.commission_percentage) / 100
  }
  return 0
})

/**
 * Policy Group `ElSelectV2` remote search method.
 *
 * @param query policy group filter (search query).
 */
const searchPolicyGroups = async (query: string) => {
  if (query == '') return

  try {
    policyGroupSearchLoading.value = true
    const results = await getPolicyGroups({
      number: query,
      page: 1,
      per_page: 10,
    })
    policyGroupSearchResults.value = results.data.map((policyGroup: IPolicyGroup) => {
      return {
        label: policyGroup.number, // Simple label for the select
        value: policyGroup,
      }
    })
  } catch {
    NotifyFail('Policy group search failed')
  } finally {
    policyGroupSearchLoading.value = false
  }
}

// Reset form when dialog opens
watch(
  () => dialogVisible.value,
  (isVisible) => {
    if (isVisible) {
      // Reset form for create mode
      selectedPolicyGroup.value = undefined
      form.policy_group_id = undefined
      form.type = ''
      form.issue_date = ''
      form.due_date = ''
      form.policies_quantity = undefined
      form.policy_group_premium = 0
      form.policy_underwriting_fee = 0
      form.commission_percentage = undefined
      form.status = InvoiceStatus.READY_TO_SEND
      activePoliciesCount.value = 0
      uploadFile.value = undefined
      uploadRef.value?.reset()
    }
  }
)

watch(uploadFile, (newFile) => {
  form.invoice_file = newFile?.raw
  formRef.value?.clearValidate('issue_date')
  formRef.value?.clearValidate('due_date')
  formRef.value?.clearValidate('invoice_file')
})

const nonFalsyFieldValidator = (rule: any, value: any, callback: any) => {
  if (!value) {
    callback(new Error())
  }
  callback()
}

const dueDateValidator = (rule: any, value: any, callback: any) => {
  if (!value) {
    callback(new Error('Due date is required'))
  } else if (value < form.issue_date) {
    callback(new Error('Due date cannot be before issue date'))
  }
  callback()
}

const currencyValidator = (rule: any, value: any, callback: any) => {
  if (value === undefined || value === null || value === '') {
    callback(new Error('This field is required'))
  } else if (value < 0) {
    callback(new Error('Value cannot be negative'))
  } else if (value > 999999999999.99) {
    callback(new Error('Value is too large'))
  }
  callback()
}

const commissionPercentageValidator = (rule: any, value: any, callback: any) => {
  if (value !== undefined && value !== null && value !== '') {
    if (value < 0) {
      callback(new Error('Commission percentage cannot be negative'))
    } else if (value > 100) {
      callback(new Error('Commission percentage cannot exceed 100%'))
    }
  }
  callback()
}

const rules = {
  policy_group_id: [
    {
      validator: nonFalsyFieldValidator,
      required: true,
      message: 'Policy Group is required',
      trigger: ['blur', 'change'],
    },
  ],
  type: [{ required: true, message: 'Invoice Type is required', trigger: ['blur', 'change'] }],
  issue_date: [{ required: true, message: 'Issue date is required', trigger: ['blur', 'change'] }],
  due_date: [{ validator: dueDateValidator, required: true, trigger: ['blur', 'change'] }],
  policies_quantity: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (value === undefined || value === null || value === '') {
          callback(new Error('Policies quantity is required'))
        } else if (value < 1) {
          callback(new Error('Policies quantity must be at least 1'))
        } else if (value > 999999) {
          callback(new Error('Policies quantity is too large'))
        } else {
          callback()
        }
      },
      required: true,
      trigger: ['blur', 'change'],
    },
  ],
  policy_group_premium: [
    { validator: currencyValidator, message: 'Premium is required', required: true, trigger: ['blur', 'change'] },
  ],
  policy_underwriting_fee: [
    {
      validator: currencyValidator,
      message: 'Underwriting fee is required',
      required: true,
      trigger: ['blur', 'change'],
    },
  ],
  commission_percentage: [{ validator: commissionPercentageValidator, required: false, trigger: ['blur', 'change'] }],
  status: [
    { validator: nonFalsyFieldValidator, required: true, message: 'Status is required', trigger: ['blur', 'change'] },
  ],
  invoice_file: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (uploadFile.value?.status === 'ready' || form.invoice_file) {
          callback()
        } else {
          callback()
        }
      },
      required: false,
      trigger: ['blur', 'change'],
    },
  ],
}

const onSubmit = async () => {
  // Validate form before submission
  await formRef.value?.validate(async (valid: boolean) => {
    if (!valid) {
      return
    }

    try {
      await handleCreateInvoice(form as ICreatePremiumInvoicePayload)
      dialogVisible.value = false
    } catch {
      // Error is handled by mutation error handler
    }
  })
}

const onClosed = () => {
  formRef.value?.resetFields()
  policyGroupBinding.value = undefined
  uploadRef.value?.reset()
  emit('closed')
}

const invoiceStatuses = Object.values(InvoiceStatus)
</script>

<template>
  <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" align-center @closed="onClosed">
    <el-form ref="formRef" :model="form" :rules="rules" label-position="left" label-width="auto">
      <div class="flex flex-col gap-5">
        <!-- Policy Group Information -->
        <el-form-item label="Policy Group" prop="policy_group_id" class="!mb-0 !w-auto grow">
          <el-select-v2
            v-model="policyGroupBinding"
            data-testid="policy-group-select"
            :options="policyGroupSearchResults"
            value-key="id"
            remote
            :remote-method="searchPolicyGroups"
            :loading="policyGroupSearchLoading"
            no-data-text="No data"
            placeholder="Policy Group"
            clearable
            filterable
            :item-height="96"
          >
            <template #default="{ item }">
              <div class="h-20 flex flex-col justify-center gap-2">
                <div class="flex items-center">
                  <span class="font-semibold text-sm text-gray-900 dark:text-gray-100">{{ item.value.number }}</span>
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-15">Customer:</span>
                    <span
                      class="text-xs text-gray-900 dark:text-gray-100 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                      >{{ item.value.customer.name }}</span
                    >
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-15">Status:</span>
                    <span
                      class="text-xs text-gray-900 dark:text-gray-100 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                      >{{ item.value.status || 'N/A' }}</span
                    >
                  </div>
                </div>
              </div>
            </template>
            <template #loading>
              <div class="w-full inline-flex items-center justify-center">
                <Icon icon="eos-icons:three-dots-loading" height="20" />
              </div>
            </template>
          </el-select-v2>
        </el-form-item>

        <!-- Invoice Type -->
        <el-form-item label="Type" prop="type" class="!mb-0 !w-auto grow">
          <el-select v-model="form.type" placeholder="Invoice Type">
            <el-option v-for="invType in Object.values(InvoiceType)" :key="invType" :label="invType" :value="invType" />
          </el-select>
        </el-form-item>

        <!-- Status -->
        <el-form-item label="Status" prop="status" class="!mb-0 grow">
          <el-select v-model="form.status">
            <el-option v-for="status in invoiceStatuses" :key="status" :label="status" :value="status" />
          </el-select>
        </el-form-item>

        <!-- Issue Date -->
        <el-form-item label="Issue Date" prop="issue_date" class="!mb-0 grow">
          <el-date-picker
            v-model="form.issue_date"
            type="date"
            placeholder="Select issue date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            class="w-full"
          />
        </el-form-item>

        <!-- Due Date -->
        <el-form-item label="Due Date" prop="due_date" class="!mb-0 grow">
          <el-date-picker
            v-model="form.due_date"
            type="date"
            placeholder="Select due date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            class="w-full"
          />
        </el-form-item>

        <!-- Policies Qty -->
        <el-form-item label="Policies Qty" prop="policies_quantity" class="!mb-0 grow">
          <el-input-number
            v-model="form.policies_quantity"
            :min="1"
            :max="999999"
            :precision="0"
            placeholder="Enter policies quantity"
            class="w-full"
          />
        </el-form-item>

        <!-- Premium -->
        <el-form-item label="Premium" prop="policy_group_premium" class="!mb-0 grow">
          <ElCurrencyInput v-model="form.policy_group_premium" :min-value="0" :max-value="999999999999.99" />
        </el-form-item>

        <!-- Underwriting Fee -->
        <el-form-item label="Underwriting Fee" prop="policy_underwriting_fee" class="!mb-0 grow">
          <ElCurrencyInput v-model="form.policy_underwriting_fee" :min-value="0" :max-value="999999999999.99" />
        </el-form-item>

        <!-- Commission -->
        <el-form-item label="Commission %" prop="commission_percentage" class="!mb-0 grow">
          <el-input-number
            v-model="form.commission_percentage"
            :min="0"
            :max="100"
            :precision="2"
            :step="0.01"
            placeholder="0.00"
            class="w-full"
          />
        </el-form-item>

        <!-- Invoice File -->
        <el-form-item label="Invoice File" prop="invoice_file" class="!mb-0 grow">
          <RlUploadPdf ref="uploadRef" v-model="uploadFile" />
        </el-form-item>
      </div>
    </el-form>

    <!-- Summary Section -->
    <div v-if="selectedPolicyGroup" class="mt-6">
      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Invoice Summary</h3>

        <div class="space-y-4">
          <!-- Policy Group Info -->
          <div class="space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Policy Group:</span>
              <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedPolicyGroup.number }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Policies Quantity:</span>
              <span class="text-sm text-gray-900 dark:text-gray-100">{{ form.policies_quantity || 0 }}</span>
            </div>
          </div>

          <!-- Financial Breakdown -->
          <div v-if="form.policy_group_premium != null && form.policy_underwriting_fee != null" class="space-y-3">
            <!-- Total Premium (Grand Total) -->
            <div
              v-if="form.policy_group_premium > 0"
              class="flex justify-between items-center py-2 px-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
            >
              <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Premium (Grand Total):</span>
              <span class="text-sm font-bold text-green-600 dark:text-green-400">${{ totalPremium.toFixed(2) }}</span>
            </div>

            <!-- Deductions -->
            <div class="ml-4 space-y-2">
              <div v-if="form.policy_underwriting_fee > 0" class="flex justify-between items-center">
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">- Underwriting Fee:</span>
                <span class="text-sm text-red-600 dark:text-red-400">-${{ totalUnderwritingFee.toFixed(2) }}</span>
              </div>
              <div
                v-if="form.commission_percentage && form.commission_percentage > 0 && totalPremium > 0"
                class="flex justify-between items-center"
              >
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">- Commission:</span>
                <span class="text-sm text-red-600 dark:text-red-400">-${{ totalCommission.toFixed(2) }}</span>
              </div>
            </div>

            <!-- Profit Calculation -->
            <div v-if="totalPremium > 0" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex justify-between items-center">
                <span class="text-base font-semibold text-gray-900 dark:text-gray-100">Profit:</span>
                <span class="text-lg font-bold text-gray-900 dark:text-gray-100">
                  ${{ (totalPremium - totalUnderwritingFee - totalCommission).toFixed(2) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-4">
            <span class="text-sm text-gray-500 dark:text-gray-400"
              >Enter premium and fee values to see calculations</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Footer actions -->
    <template #footer>
      <div class="flex justify-end gap-2">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="isCreatePending" @click="onSubmit"> Create Invoice </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
:deep(.el-form-item__label) {
  width: 20%;
}
</style>
